import { LocaleLabelPipe } from 'src/app/pipes/locale-punctuation.pipe';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, input } from '@angular/core';
import {
  TRANSLOCO_SCOPE,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { QaiFloatingSelectComponent, QaiSearchableSelectComponent } from '@qai/ui';
import { AssignmentCategory } from 'src/app/interfaces/reportingV2/ipractice-assignment-stats-request';
import { ReportingLocalStorageUtil } from 'src/app/utils/reporting-localstorage.util';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';

export const DEFAULT_STATUS_FILTER = ['ACTIVE', 'COMPLETE'];

@Component({
  selector: 'app-assignment-filter',
  templateUrl: './assignment-filter.component.html',
  styleUrls: ['./assignment-filter.component.scss'],
  standalone: true,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reportingV2',
    },
  ],
  imports: [CommonModule, TranslocoModule, LocaleLabelPipe, QaiFloatingSelectComponent, QaiSearchableSelectComponent],
})
export class AssignmentFilterComponent {
  @Input() assignmentType: 'practice' | 'certification' = 'practice';
  useStatusFilter = input<boolean>(false);

  // For syncing status filter between tabs
  @Input() set syncValue(val: string[] | null) {
    if (val !== null && val !== undefined) {
      this.selectedStatuses = [...val];
    }
  }
  @Output() assignmentTypeChange = new EventEmitter<AssignmentCategory>();
  @Output() statusFilterChange = new EventEmitter<string[]>();

  assignmentOptions: { label: string; value: string }[] = [];
  selectedAssignmentType: AssignmentCategory = 'RECENT_ASSIGNMENTS';

  statusOptions: { label: string; value: string }[] = [];
  selectedStatuses: string[] | string = [...DEFAULT_STATUS_FILTER];
  scopedOptionsSelectionMode = {
    enable: true,
    selectAllOption: { value: 'selectAll', label: 'Select all' },
    deselectAllOption: { value: 'deselectAll', label: 'Deselect all' },
  };

  othersTextFormatter = (count: number): string =>
    count === 1
      ? this.translocoService.translate('OTHERS_SINGULAR', {}, 'reportingV2')
      : this.translocoService.translate('OTHERS_PLURAL', { count }, 'reportingV2');

  constructor(
    private translocoService: TranslocoService,
    private amplitudeService: AmplitudeService
  ) {}

  ngOnInit() {
    // Initialize options
    if (this.useStatusFilter()) {
      this.initStatusFilter();
    } else {
      this.initAssignmentFilter();
    }
  }

  private initAssignmentFilter(): void {
    this.assignmentOptions = [
      {
        label: this.translocoService.translate(
          'ALL_ASSIGNMENTS',
          {},
          'reportingV2'
        ),
        value: 'ALL_ASSIGNMENTS' as AssignmentCategory,
      },
      {
        label: this.translocoService.translate(
          'RECENT_ASSIGNMENTS',
          {},
          'reportingV2'
        ),
        value: 'RECENT_ASSIGNMENTS' as AssignmentCategory,
      },
    ];

    // Load stored value or use default
    let storedFilter;
    if (this.assignmentType === 'practice') {
      storedFilter =
        ReportingLocalStorageUtil.getStoredPracticeAssignmentFilter();
    } else if (this.assignmentType === 'certification') {
      storedFilter =
        ReportingLocalStorageUtil.getStoredCertificationAssignmentFilter();
    }

    this.selectedAssignmentType =
      (storedFilter as AssignmentCategory) || 'RECENT_ASSIGNMENTS';

    if (!storedFilter) {
      if (this.assignmentType === 'practice') {
        ReportingLocalStorageUtil.setStoredPracticeAssignmentFilter(
          'RECENT_ASSIGNMENTS',
        );
      } else if (this.assignmentType === 'certification') {
        ReportingLocalStorageUtil.setStoredCertificationAssignmentFilter(
          'RECENT_ASSIGNMENTS',
        );
      }
    }

    // Emit initial value
    this.assignmentTypeChange.emit(this.selectedAssignmentType);
  }

  onAssignmentChange(event: string): void {
    
    // Track assignment filter click
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.REPORTINGDASHBOARD_CLICK_ASSIGNMENTFILTER);
    this.selectedAssignmentType = event as AssignmentCategory;

    if (this.assignmentType === 'practice') {
      ReportingLocalStorageUtil.setStoredPracticeAssignmentFilter(event);
    } else if (this.assignmentType === 'certification') {
      ReportingLocalStorageUtil.setStoredCertificationAssignmentFilter(event);
    }
    // Emit the updated value
    this.assignmentTypeChange.emit(event as AssignmentCategory);
  }

  private initStatusFilter(): void {
    this.statusOptions = [
      { label: this.translocoService.translate('STATUS_ACTIVE', {}, 'reportingV2'), value: 'ACTIVE' },
      { label: this.translocoService.translate('STATUS_ENDED', {}, 'reportingV2'), value: 'COMPLETE' },
      { label: this.translocoService.translate('STATUS_CLOSED', {}, 'reportingV2'), value: 'CLOSED' },
      { label: this.translocoService.translate('STATUS_ARCHIVED', {}, 'reportingV2'), value: 'ARCHIVED' },
    ];
    this.scopedOptionsSelectionMode.selectAllOption.label = this.translocoService.translate('SELECT_ALL', {}, 'reportingV2');
    this.scopedOptionsSelectionMode.deselectAllOption.label = this.translocoService.translate('DESELECT_ALL', {}, 'reportingV2');

    const storedStatuses = ReportingLocalStorageUtil.getStoredRollupStatusFilter();

    this.selectedStatuses = storedStatuses ?? [...DEFAULT_STATUS_FILTER];

    if (!storedStatuses) {
      this.persistStatusFilter(this.selectedStatuses);
    }

    this.statusFilterChange.emit(this.selectedStatuses);
  }

  onStatusChange(event: string | string[]): void {
    const statuses = Array.isArray(event) ? event : [event];
    this.selectedStatuses = statuses;
    this.persistStatusFilter(statuses);
    this.statusFilterChange.emit(statuses);
  }

  private persistStatusFilter(statuses: string[]): void {
    ReportingLocalStorageUtil.setStoredRollupStatusFilter(statuses);
  }
}
